import { z } from 'zod';
import { moduleKeySchema } from '../../enums/module-key.js';
import {
  entitlementStatusSchema,
  organizationKindSchema,
  organizationLimitsSchema,
  organizationStatusSchema,
  planKeySchema,
} from '../../enums/tenancy.js';

/** Console · modules of one organization (phase F, ADR 0012). The platform decides whether a
 *  module is available (`entitlement`); the organization switches on what it has (`activated`,
 *  on by default when the module is granted). Effective = core ∨ (available ∧ activated). */
export const consoleModuleSchema = z.object({
  key: moduleKeySchema,
  isCore: z.boolean(),
  implemented: z.boolean(),
  entitlement: entitlementStatusSchema,
  trialEndsAt: z.string().datetime().nullable(),
  /** The organization's own switch; meaningful only while the module is available. */
  activated: z.boolean(),
  enabled: z.boolean(),
  /** Last change of availability, by the platform. */
  changedAt: z.string().datetime().nullable(),
});
export type ConsoleModule = z.infer<typeof consoleModuleSchema>;

export const consoleModuleListSchema = z.object({
  organizationId: z.string().uuid(),
  items: z.array(consoleModuleSchema),
});

/** The module switch of the console: `included` and `trial` make it available, `none` takes
 *  it away. A trial needs its end. */
export const moduleAvailabilitySchema = z.object({
  entitlement: entitlementStatusSchema,
  trialEndsAt: z.string().datetime().nullable().optional(),
});
export type ModuleAvailability = z.infer<typeof moduleAvailabilitySchema>;

/** Console · an organization as the platform sees it: contract, limits and what it uses. */
export const organizationUsageSchema = z.object({
  /** Active memberships (invited and suspended people do not count). */
  users: z.number().int(),
  /** Missions with status `open`, drills included. */
  activeMissions: z.number().int(),
  devices: z.number().int(),
  /** Megabytes of files kept (attachments of Comunicazioni). */
  storageMb: z.number(),
});

export const consoleOrganizationSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  kind: z.string(),
  status: organizationStatusSchema,
  planKey: planKeySchema.nullable(),
  emailDomains: z.array(z.string()),
  limits: organizationLimitsSchema,
  usage: organizationUsageSchema,
  suspendedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export type ConsoleOrganization = z.infer<typeof consoleOrganizationSchema>;
export const consoleOrganizationListSchema = z.object({
  items: z.array(consoleOrganizationSchema),
});

/** Assigning a package: its modules become available and its limits the starting values. */
export const planAssignmentSchema = z.object({ planKey: planKeySchema });

const slugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3)
  .max(60)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);
const domainSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9-]+(?:\.[a-z0-9-]+)+$/);

/** Console · a new organization: its identity, its package and its first Super Admin, invited
 *  by e-mail. Everything is created in one transaction (phase F, `provisionOrganization`). */
export const organizationProvisionSchema = z.object({
  name: z.string().trim().min(3).max(120),
  slug: slugSchema,
  kind: organizationKindSchema,
  planKey: planKeySchema,
  emailDomains: z.array(domainSchema).max(10).default([]),
  defaultLocale: z.enum(['it', 'en']).default('it'),
  timezone: z.string().trim().min(3).max(64).default('Europe/Rome'),
  superAdmin: z.object({
    email: z.string().trim().toLowerCase().email(),
    displayName: z.string().trim().min(2).max(120),
  }),
  invitationDays: z.number().int().min(1).max(30).default(7),
});
export type OrganizationProvision = z.infer<typeof organizationProvisionSchema>;
